import { useState, useEffect, useCallback } from 'react';
import {
  Download,
  Trash2,
  FileImage,
  FileCode,
  Clock,
  AlertCircle,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { fetchExportHistory, getSignedUrls, deleteExport } from '../../../lib/exportStorage';
import type { ExportRecord } from '../../../types/export';

interface ExportHistoryPanelProps {
  projectId: string;
  compact?: boolean;
  maxItems?: number;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ExportHistoryPanel({
  projectId,
  compact = false,
  maxItems = 5,
}: ExportHistoryPanelProps) {
  const [records, setRecords] = useState<ExportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchExportHistory(projectId, compact ? maxItems : 50);
      setRecords(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load export history');
    } finally {
      setLoading(false);
    }
  }, [projectId, compact, maxItems]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleDownload = async (record: ExportRecord, type: 'background' | 'overlay') => {
    try {
      setDownloadingId(`${record.id}-${type}`);
      const urls = await getSignedUrls(
        record.project_id,
        record.id,
        record.export_config.format,
        record.export_config.scale
      );
      const signedUrl = type === 'background' ? urls.backgroundUrl : urls.overlayUrl;
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (record: ExportRecord) => {
    if (confirmDeleteId !== record.id) {
      setConfirmDeleteId(record.id);
      return;
    }

    try {
      setDeletingId(record.id);
      setConfirmDeleteId(null);
      await deleteExport(
        record.id,
        record.project_id,
        record.export_config.format,
        record.export_config.scale
      );
      setRecords((prev) => prev.filter((r) => r.id !== record.id));
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (!confirmDeleteId) return;
    const timeout = setTimeout(() => setConfirmDeleteId(null), 5000);
    return () => clearTimeout(timeout);
  }, [confirmDeleteId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="animate-spin text-white/30" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/5 border border-red-500/20 text-red-400 text-sm">
        <AlertCircle size={16} />
        {error}
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="text-center py-8">
        <FileImage size={32} className="mx-auto text-white/10 mb-3" />
        <p className="text-sm text-white/30">No exports yet</p>
        <p className="text-xs text-white/20 mt-1">Export your first asset from the editor</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {records.map((record) => {
        const isExpanded = expandedId === record.id;
        const isDeleting = deletingId === record.id;
        const isConfirmingDelete = confirmDeleteId === record.id;

        return (
          <div
            key={record.id}
            className="rounded-lg bg-[#12121a] border border-white/[0.06] overflow-hidden"
          >
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors"
              onClick={() => setExpandedId(isExpanded ? null : record.id)}
            >
              {record.status === 'complete' ? (
                <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
              ) : (
                <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white truncate">
                    {record.export_config.format.toUpperCase()}
                    {record.export_config.scale > 1 && ` @${record.export_config.scale}x`}
                  </span>
                  <span className="text-xs text-white/30">
                    {record.dimensions.width} x {record.dimensions.height}
                  </span>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <Clock size={10} className="text-white/20" />
                  <span className="text-xs text-white/30">{formatDate(record.created_at)}</span>
                </div>
              </div>

              {!compact && (
                <div className="flex items-center gap-1.5">
                  {record.background_url && (
                    <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-500/10 text-blue-400">
                      IMG{' '}
                      {record.file_sizes.background
                        ? formatBytes(record.file_sizes.background)
                        : ''}
                    </span>
                  )}
                  {record.overlay_url && (
                    <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-500/10 text-emerald-400">
                      JS{' '}
                      {record.file_sizes.overlay ? formatBytes(record.file_sizes.overlay) : ''}
                    </span>
                  )}
                </div>
              )}

              {isExpanded ? (
                <ChevronUp size={16} className="text-white/30 flex-shrink-0" />
              ) : (
                <ChevronDown size={16} className="text-white/30 flex-shrink-0" />
              )}
            </div>

            {isExpanded && (
              <div className="px-4 pb-3 pt-1 border-t border-white/[0.06] space-y-3">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <span className="text-white/40">Format</span>
                  <span className="text-white text-right uppercase">
                    {record.export_config.format}
                  </span>
                  <span className="text-white/40">Quality</span>
                  <span className="text-white text-right capitalize">
                    {record.export_config.quality}
                  </span>
                  <span className="text-white/40">Scale</span>
                  <span className="text-white text-right">{record.export_config.scale}x</span>
                  <span className="text-white/40">Dimensions</span>
                  <span className="text-white text-right">
                    {record.dimensions.width} x {record.dimensions.height}px
                  </span>
                  {record.file_sizes.background && (
                    <>
                      <span className="text-white/40">Background Size</span>
                      <span className="text-white text-right">
                        {formatBytes(record.file_sizes.background)}
                      </span>
                    </>
                  )}
                  {record.file_sizes.overlay && (
                    <>
                      <span className="text-white/40">Overlay Size</span>
                      <span className="text-white text-right">
                        {formatBytes(record.file_sizes.overlay)}
                      </span>
                    </>
                  )}
                </div>

                {record.status === 'failed' && record.error_message && (
                  <div className="p-2 rounded bg-red-500/5 border border-red-500/20 text-xs text-red-400">
                    {record.error_message}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                  {record.background_url && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(record, 'background');
                      }}
                      disabled={downloadingId === `${record.id}-background`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 text-xs font-medium hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                    >
                      {downloadingId === `${record.id}-background` ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Download size={12} />
                      )}
                      Background
                    </button>
                  )}
                  {record.overlay_url && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(record, 'overlay');
                      }}
                      disabled={downloadingId === `${record.id}-overlay`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-medium hover:bg-emerald-500/20 transition-colors disabled:opacity-50"
                    >
                      {downloadingId === `${record.id}-overlay` ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Download size={12} />
                      )}
                      Overlay
                    </button>
                  )}

                  <div className="flex-1" />

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(record);
                    }}
                    disabled={isDeleting}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      isConfirmingDelete
                        ? 'bg-red-500/20 text-red-400'
                        : 'text-white/30 hover:text-red-400 hover:bg-red-500/10'
                    } disabled:opacity-50`}
                  >
                    {isDeleting ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Trash2 size={12} />
                    )}
                    {isConfirmingDelete ? 'Confirm Delete' : 'Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
